/**
 * One WebSocket connection with reconnect, clock offset and acknowledged commands.
 * Lives outside React (StrictMode would otherwise open two sockets).
 */
import { versionAction, type VersionAction } from './version.ts';

export type Status = 'connecting' | 'open' | 'closed' | 'denied';

export interface Ack {
  ok: boolean;
  reason?: string;
}

type Listener = () => void;

export class LiveSocket<V> {
  view: V | null = null;
  status: Status = 'connecting';
  reason: string | null = null;
  offset = 0; // serverNow - clientNow
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private pending = new Map<string, (a: Ack) => void>();
  private retry = 0;
  private pingTimer: number | null = null;
  private lastPong = Date.now();
  private stopped = false;
  private offsets: number[] = [];
  private serverVersion: unknown = null;
  private versionStatus: VersionAction = 'invalid';

  /**
   * @param url    path + query of the socket endpoint
   * @param check  optional: asked after repeated failures whether the link is still valid;
   *               resolving false marks the socket 'denied' (seat released, link expired).
   */
  constructor(
    private readonly url: string,
    private readonly check?: () => Promise<boolean>,
  ) {
    this.connect();
  }

  subscribe = (fn: Listener): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  private emit(): void {
    for (const l of this.listeners) l();
  }

  private connect(): void {
    if (this.stopped) return;
    this.serverVersion = null;
    this.versionStatus = 'invalid';
    this.status = 'connecting';
    this.emit();
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${location.host}${this.url}`);
    this.ws = ws;
    ws.onopen = () => {
      if (this.ws !== ws || this.stopped) return;
      this.retry = 0;
      this.status = 'open';
      this.lastPong = Date.now();
      this.emit();
      this.ping();
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.pingTimer = window.setInterval(() => {
        if (Date.now() - this.lastPong > 35_000) ws.close();
        else this.ping();
      }, 15_000);
    };
    ws.onmessage = (ev) => {
      if (this.ws !== ws || this.stopped) return;
      let msg: { k: string; view?: V; id?: string; ok?: boolean; reason?: string; t?: number; serverNow?: number; phase?: string };
      try {
        msg = JSON.parse(String(ev.data));
      } catch {
        return;
      }
      if (msg.k === 'version') {
        this.serverVersion = msg;
        this.checkVersion(msg.phase ?? '');
      } else if (msg.k === 'view') {
        this.checkVersion((msg.view as { clock?: { phase?: string } } | undefined)?.clock?.phase ?? '');
        if (this.versionStatus !== 'current') return;
        this.view = msg.view ?? null;
        if (msg.serverNow && this.offsets.length === 0) this.offset = msg.serverNow - Date.now();
        this.emit();
      } else if (msg.k === 'error') {
        this.reason = msg.reason ?? 'This session cannot be opened.';
        console.warn('Session connection refused:', this.reason);
        this.view = null;
        this.stopped = true;
        this.status = 'denied';
        this.emit();
      } else if (msg.k === 'ack' && msg.id) {
        this.pending.get(msg.id)?.({ ok: !!msg.ok, reason: msg.reason });
        this.pending.delete(msg.id);
      } else if (msg.k === 'pong' && msg.t && msg.serverNow) {
        this.lastPong = Date.now();
        const rtt = Date.now() - msg.t;
        this.offsets.push(msg.serverNow + rtt / 2 - Date.now());
        if (this.offsets.length > 5) this.offsets.shift();
        const sorted = [...this.offsets].sort((a, b) => a - b);
        this.offset = sorted[Math.floor(sorted.length / 2)]!;
      }
    };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      if (!this.stopped) {
        this.serverVersion = null;
        this.versionStatus = 'invalid';
        window.dispatchEvent(new CustomEvent('nightshift-version', { detail: { action: 'invalid', build: null } }));
      }
      if (this.pingTimer) clearInterval(this.pingTimer);
      for (const [, fn] of this.pending) fn({ ok: false, reason: 'Connection lost. Try again.' });
      this.pending.clear();
      if (this.reason || ev.code === 1008 || ev.code === 4001 || (ev.code === 1006 && this.status === 'connecting' && this.retry > 3)) {
        // server refused (released seat / expired link) after several tries
        if (this.reason || ev.code === 4001 || ev.code === 1008) {
          this.status = 'denied';
          this.emit();
          return;
        }
      }
      this.status = 'closed';
      this.emit();
      const wait = Math.min(8000, 500 * 2 ** this.retry++);
      // A refused upgrade looks like 1006 to the browser: ask the server why.
      if (this.check && this.retry >= 2) {
        this.check()
          .then((valid) => {
            if (valid) window.setTimeout(() => this.connect(), wait);
            else {
              this.status = 'denied';
              this.emit();
            }
          })
          .catch(() => window.setTimeout(() => this.connect(), wait));
        return;
      }
      window.setTimeout(() => this.connect(), wait);
    };
  }

  private ping(): void {
    this.ws?.readyState === WebSocket.OPEN && this.ws.send(JSON.stringify({ k: 'ping', t: Date.now() }));
  }

  private checkVersion(phase: string): void {
    this.versionStatus = versionAction(this.serverVersion, phase);
    const build = this.versionStatus === 'invalid' ? null : (this.serverVersion as { build: string }).build;
    if (this.versionStatus === 'invalid') console.warn('Server sent a view without valid deployment metadata.');
    window.dispatchEvent(new CustomEvent('nightshift-version', { detail: { action: this.versionStatus, build } }));
  }

  /** Applies the current connection's command gate to HTTP mutations too. */
  get commandError(): string | null {
    if (this.reason) return this.reason;
    if (this.versionStatus !== 'current') return 'Reload to connect to the current version before sending commands.';
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return 'Reconnecting — try again in a moment.';
    return null;
  }

  send(cmd: unknown): Promise<Ack> {
    const reason = this.commandError;
    if (reason) return Promise.resolve({ ok: false, reason });
    const id = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
    return new Promise((resolve) => {
      this.pending.set(id, resolve);
      this.ws!.send(JSON.stringify({ k: 'cmd', id, cmd }));
      window.setTimeout(() => {
        if (this.pending.has(id)) {
          this.pending.delete(id);
          resolve({ ok: false, reason: 'No answer from the server.' });
        }
      }, 8000);
    });
  }

  sub(table: number | null): void {
    this.ws?.readyState === WebSocket.OPEN && this.ws.send(JSON.stringify({ k: 'sub', table }));
  }

  now(): number {
    return Date.now() + this.offset;
  }

  close(): void {
    this.stopped = true;
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.ws?.close();
  }
}
